<template>
  <GlossaryTopbar />
  <div class="glossaries-container">
    <div class="header">
      <TextBody i18n-key="glossaries.descriptionHeader" tag="span" :body-type="3" />
      <BbBtn
        v-has-access:hide="{ resource: RESOURCE_TYPE.GLOSSARIES, resource_action: RESOURCE_ACTION_TYPE.CREATE }"
        size="small"
        type="primary"
        @click="openCreateGlossaryPopup"
        color="#6222e3"
      >
        <template #prepend>
          <BbIcon size="small" name="plusincircle" />
        </template>
        <TextBody i18n-key="glossaries.createNew" />
      </BbBtn>
    </div>

    <div v-if="glossaries.length" class="row-view">
      <div :class="['column-view', 'columnName']">{{ t('glossaries.glossaryName') }}</div>
      <div :class="['column-view', 'columnName']">{{ t('glossaries.public') }}?</div>
      <div :class="['column-view', 'columnName']">{{ t('common.items') }}</div>
      <div :class="['column-view', 'columnName']">{{ t('common.modified') }}</div>
      <div :class="['column-view', 'columnName']">{{ t('common.modifiedBy') }}</div>
      <div :class="['column-view', 'columnName']"></div>
    </div>

    <hr class="separator" />

    <div class="glossaries-content" v-if="!isLoading">
      <div v-for="(glossary, index) in glossaries" :key="`glossary-${index}`" class="glossary-wrapper">
        <div class="row-view">
          <div class="column-view">
            <span>{{ glossary.title }}</span>
          </div>
          <div class="column-view">
            <div v-if="glossary.isPublic" class="public-container">
              <BbIcon size="small" name="MLR-unmodified" type="filled" />
              <span>{{ t('common.yes') }}</span>
            </div>
            <div v-else class="public-container">
              <BbIcon size="small" name="MLR-modified" type="filled" />
              <span>{{ t('common.no') }}</span>
            </div>
          </div>
          <div class="column-view">
            <span>{{ itemsCount(glossary) }} {{ t('common.items') }}</span>
          </div>
          <div class="column-view">
            <span>{{ formatTimestampToYearAndMonth(glossary.modifiedAt) }}</span>
          </div>
          <div class="column-view">
            <span>{{ glossary.userName }}</span>
          </div>
          <div class="column-view">
            <BbBtn
              v-has-access:hide="{ resource: RESOURCE_TYPE.GLOSSARIES, resource_action: RESOURCE_ACTION_TYPE.UPDATE }"
              size="small"
              type="secondary"
              @click="editGlossary(glossary)"
            >
              <BbIcon size="small" name="edit" />
              <TextBody i18n-key="common.edit" />
            </BbBtn>
            <ActionsMenu
              v-has-access:hide="{ resource: RESOURCE_TYPE.GLOSSARIES, resource_action: RESOURCE_ACTION_TYPE.DELETE }"
              :id="glossary.id"
              @remove="onRemoveGlossary"
            />
          </div>
        </div>
        <hr class="separator" />
      </div>
    </div>
    <LoadingIndicator v-else class="loading-spinner" />
    <GlossarySavePopup
      :popup-opened="isNewGlossaryPopupOpened"
      @update:open="isNewGlossaryPopupOpened = false"
      @execute:action="editGlossary"
    />
    <div v-if="!glossaries.length && !isLoading" class="empty-state">
      <TextBody i18n-key="glossaries.noGlossariesFound" tag="span" weight="medium" :body-type="2" />
      <div
        v-has-access:hide="{ resource: RESOURCE_TYPE.GLOSSARIES, resource_action: RESOURCE_ACTION_TYPE.CREATE }"
        class="create-new-container"
      >
        <TextBody i18n-key="glossaries.createOneNow" tag="span" weight="medium" :body-type="2" />
        <BbBtn size="small" type="primary" @click="openCreateGlossaryPopup" color="#6222e3">
          <template #prepend>
            <BbIcon size="small" name="plusincircle" />
          </template>
          <TextBody i18n-key="glossaries.createNew" />
        </BbBtn>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatTimestampToYearAndMonth } from '~/utils/date-utils';
import useGlossaries from '~/composables/useGlossaries';
import { RESOURCE_TYPE, RESOURCE_ACTION_TYPE } from '~/constants/user';
import GlossaryTopbar from '~/components/glossary/GlossaryTopbar';
import ActionsMenu from '~/components/common/ActionsMenu';
import GlossarySavePopup from '~/components/glossary/GlossarySavePopup';
import LoadingIndicator from '~/components/common/LoadingIndicator';
import TextBody from '~/components/common/TextBody';

export default {
  props: {
    glossary: {
      type: Object,
      default: () => {},
    },
  },
  components: { GlossarySavePopup, TextBody, GlossaryTopbar, ActionsMenu, LoadingIndicator },
  emits: ['open-glossary', 'select', 'save', 'cancel'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const descriptionHeader = t('glossaries.descriptionHeader');

    const glossaries = ref([]);
    const glossaryTitle = ref('');
    const isNewGlossaryPopupOpened = ref(false);
    const isLoading = ref(false);

    const { getArcaneGlossaries, removeGlossary } = useGlossaries();

    const rules = [(v) => !!v || t('modals.titleShouldNotBeEmpty')];

    const onCancel = () => {
      isNewGlossaryPopupOpened.value = false;
      glossaryTitle.value = '';
    };
    const openCreateGlossaryPopup = () => (isNewGlossaryPopupOpened.value = true);

    const editGlossary = (glossary) => {
      isNewGlossaryPopupOpened.value = false;
      // update case
      if (glossary.id) {
        emit('open-glossary', glossary);
      }
      // create case
      else {
        emit('open-glossary', glossary);
      }
    };

    const onRemoveGlossary = async (glossaryId) => {
      glossaries.value = glossaries.value.filter(({ id }) => id !== glossaryId);
      await removeGlossary(glossaryId);
    };

    const itemsCount = (glossary) => glossary.entries?.[0]?.phrases?.length ?? 0;

    onMounted(async () => {
      isLoading.value = true;
      glossaries.value = await getArcaneGlossaries();
      isLoading.value = false;
    });

    return {
      openCreateGlossaryPopup,
      descriptionHeader,
      formatTimestampToYearAndMonth,
      glossaries,
      glossaryTitle,
      isLoading,
      isNewGlossaryPopupOpened,
      itemsCount,
      onRemoveGlossary,
      RESOURCE_ACTION_TYPE,
      RESOURCE_TYPE,
      rules,
      onCancel,
      editGlossary,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.glossaries-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background-color: $bb-neutral-0;
  padding: $bb-spacing-4;

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    justify-content: center;
    gap: $bb-spacing-2;

    .create-new-container {
      display: flex;
      flex-direction: column;
      gap: $bb-spacing-2;
    }

    span {
      font-size: $bb-spacing-6;
      line-height: $bb-spacing-7;
    }
  }

  .loading-spinner {
    background-color: $bb-neutral-0;
  }

  .header {
    display: flex; /* Use flexbox layout */
    justify-content: space-between; /* Align items at the beginning and the end */
    flex-wrap: wrap; /* Allow items to wrap to the next line if necessary */
    width: 100%; /* Adjust width as needed */
    padding: $bb-spacing-2; /* Optional padding */
    margin-bottom: $bb-spacing-5;

    :deep(span) {
      max-width: 500px;
    }
  }

  .glossaries-content {
    .glossary-wrapper {
      padding: $bb-spacing-5 0;
    }
  }

  .row-view {
    display: flex;
    align-items: center;
  }

  .column-view {
    display: flex;
    align-items: center;
    flex: 1;

    .public-container {
      display: flex;
      gap: 2px;
    }

    &.columnName {
      @include bb-medium-text-styles('md');
      color: $bb-neutral-2;
    }

    &:last-child {
      // flex: 0.1;
    }
  }

  .separator {
    margin-top: $bb-spacing-5;
    background: $bb-neutral-4;
    height: 1px;
    border: none;
  }
}
</style>
